import React, { memo, useState, useEffect, useRef } from 'react';
import cn from 'classnames';
import { useTranslation } from 'next-i18next';
import { debounce } from 'lodash';

import { Spinner } from 'components';
import SelectionTickMark from 'components/form/SelectionTickMark';
import { useVisibleOnIntersect } from 'hooks/useVisibleOnIntersect';
import useScrollOffset from 'hooks/useScrollOffset';
import { useResponsive } from 'hooks/useResponsive';
import { useNotification } from 'hooks/useNotification';
import { useCandidate } from 'hooks/useCandidate';
import { useCandidateOnboarding } from 'hooks/useCandidateOnboarding';
import { Option } from 'lib/models/option';
import { MATCHING_PREFERENCE_STEPS } from 'lib/models/candidate';
import { Footer } from '../Footer/Footer.component';
import SidebarMP from '../SidebarMP';
import MobileHeaderMp from '../MobileHeaderMp';

import styles from '../Onboarding.module.scss';

export interface RoleLevelProps {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
  roleLevels: Array<Omit<Option, 'id'> & { id: number }> | undefined;
  candidateSelectedLevel: Array<number> | null;
  onNextClick: ({
    experience_preferences,
    isBackClicked,
  }: {
    experience_preferences: Array<number>;
    isBackClicked?: boolean;
  }) => void;
  isLoading: boolean;
  isBackLoading: boolean;
  isSquareCheckbox: boolean;
  isMatchingPreferences?: boolean;
  onStepSelect?: (step: MATCHING_PREFERENCE_STEPS) => void;
}

export const RoleLevel: React.FC<RoleLevelProps> = memo(
  ({
    className = '', // custom class name
    style, // custom style
    candidateSelectedLevel,
    onNextClick,
    isLoading,
    isBackLoading,
    isSquareCheckbox,
    roleLevels,
    isMatchingPreferences,
    onStepSelect,
  }: RoleLevelProps) => {
    const [t] = useTranslation('candidate-onboarding');
    const bottomRef = useRef<HTMLDivElement>(null);
    const { isWindowScrolled } = useScrollOffset();
    const screens = useResponsive();
    const notificationInstance = useNotification();
    const { updateCandidateInfo } = useCandidate();

    const contentClassNames = cn(styles.container, className);

    const [selectedLevel, setSelectedLevel] = useState<Array<number>>([]);
    const [fullyScrolled, setFullyScrolled] = useState(false);

    useVisibleOnIntersect({
      elRef: bottomRef,
      threshold: 0,
      onIntersect: () => setFullyScrolled(true),
      onHidden: () => setFullyScrolled(false),
      rootMargin: '0px',
    });

    useEffect(() => {
      if (candidateSelectedLevel?.length) {
        setSelectedLevel(candidateSelectedLevel);
      }
    }, [candidateSelectedLevel]);

    const isReady = !!selectedLevel.length;
    const { isValueEdited, setValueEdited, onBackClickMPMobile } =
      useCandidateOnboarding({ isReady });

    const apiCallToUpdate = async (modifiedLevel: Array<number>) => {
      try {
        await updateCandidateInfo({
          partialCandidateAttributes: {
            experience_preferences: modifiedLevel,
          },
        });
      } catch (error) {
        notificationInstance.handleExceptionError(error);
      }
    };

    const debouncedAPICall = React.useRef(
      debounce(async (modifiedLevel) => {
        await apiCallToUpdate(modifiedLevel);
      }, 3000)
    ).current;

    const onSubmit = (isBackClicked?: boolean) => {
      setValueEdited(false);
      onNextClick({ experience_preferences: selectedLevel, isBackClicked });
    };
    const onLevelCheckboxClick = (levelType: number) => {
      setValueEdited(true);

      const index = selectedLevel?.findIndex((type) => type === levelType);
      let modifiedLevel = [];
      if (index === -1) modifiedLevel = [...selectedLevel, levelType];
      else
        modifiedLevel =
          selectedLevel && selectedLevel.filter((item) => item !== levelType);

      /** For MP
       * Desktop: Autosave on every change
       *          Do not make changes when nothing is selected on mandatory step
       * Mobile: Save on CTA click
       */
      setSelectedLevel(modifiedLevel);
      if (modifiedLevel.length === 0) return;

      if (isMatchingPreferences && !screens.sm) {
        debouncedAPICall(modifiedLevel);
      }
    };

    const onChevronClick = () => {
      if (isValueEdited) {
        onBackClickMPMobile();
      } else {
        onSubmit(false);
      }
    };

    if (!roleLevels) {
      return (
        <div className={styles.spinner}>
          <Spinner />
        </div>
      );
    }

    return (
      <div>
        <MobileHeaderMp
          title={t('matching-preferences.experience-level')}
          onChevronClick={onChevronClick}
        />

        <div
          className={contentClassNames}
          style={style}
          data-testid="RoleLevel"
        >
          {isMatchingPreferences && onStepSelect && (
            <SidebarMP
              isReady={isReady}
              className={styles.sidebar}
              onStepSelect={onStepSelect}
            />
          )}
          <div
            className={cn(styles.content, {
              [styles['content-mp']]: isMatchingPreferences,
            })}
          >
            <p className={styles.setup}>{t('roles-level.title')}</p>
            <p className={styles.desc}>{t('roles-level.subtitle')}</p>
            <div className={cn(styles.checkboxes)}>
              {roleLevels?.map(({ id, label }) => (
                <SelectionTickMark
                  isTickSquare={isSquareCheckbox}
                  key={id}
                  checked={selectedLevel?.some((levelId) => levelId === id)}
                  onChange={() => onLevelCheckboxClick(id)}
                  className={cn(styles.selectionbox)}
                  isDisabled={
                    selectedLevel.length > 1 &&
                    !selectedLevel?.some((levelId) => levelId === id)
                  }
                >
                  {label}
                </SelectionTickMark>
              ))}
            </div>
            <div className={styles['bottom-ref']} ref={bottomRef}></div>
          </div>

          <Footer
            isBackLoading={isBackLoading}
            isLoading={isLoading}
            isReady={isReady}
            onSubmit={onSubmit}
            isWindowScrolled={isWindowScrolled}
            isFullyScrolled={fullyScrolled}
            isMatchingPreference={isMatchingPreferences}
          />
        </div>
      </div>
    );
  }
);

RoleLevel.displayName = 'RoleLevel';
